import { useMemo, useEffect } from "react";
import {
  Badge,
  Col,
  Container,
  Row,
  Tab,
  Tabs,
  Button,
  Card,
  Form,
} from "react-bootstrap";
import { cents, Money, showMoney, toCents } from "./Data/Money";
import { useParamIds } from "./Data/Common";
import { ColDef, GridReadyEvent, RowClickedEvent } from "ag-grid-community";
import { useLoaderData, useLocation, useNavigate } from "react-router-dom";
import { PageTitle } from "./Controls/PageTitle";
import {
  FacilitySub,
  ParkerDetailsGridResult,
  ParkerListEntry,
} from "./Data/ApiTypes";
import { SubscriptionStatus } from "./Data/ApiTransport";
import { showSubscriptionStatus } from "./Data/Subscription";
import {
  Asterisk,
  Link45deg,
  Trash,
  Envelope,
  PersonAdd,
  ExclamationCircleFill,
} from "react-bootstrap-icons";
import { Instant } from "@js-joda/core";
import {
  colInstantDateTime,
  colMoney,
  colNumber,
  DataGrid,
  compareMoney,
  colEnum,
} from "./Controls/DataGrid";
import { add } from "dinero.js";
import { ToolButton } from "./Controls/PageBar";
import { useState } from "react";
import { toast } from "react-toastify";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { InviteLinkDialog } from "./Controls/InviteLinkDialog";
import { ConfirmDialog } from "./Controls/ConfirmDialog";
import { ServerRequestsWaitlistData } from "./shared/api/axios-client";
import WaitlistManagementPage from "./WaitlistManagementPage";

enum MonthlyParkerTab {
  Applied = "applied", //waiting for approval
  Waiting = "waiting", //waiting for contract
  Active = "active", //active
  Invited = "invited",
  Waitlist = "waitlist",
}

export interface GridParker {
  parkerId: number;
  name: string;
  note: string;
  emailAddress: string;
  phoneNumber: string | null;
  guestCount: number;
  spotCount: number;
  planCount: number;
  balance: Money;
  mrr: Money;
  planName: string;
  createdWhen: Instant;
  unitPrice: Money;
  isExpired: boolean;
  subscriptionId: number;
  inviteId: number;
  hasAutoPay: boolean;
  hasNonRenewedPeriod: boolean;
  status: SubscriptionStatus;
}

const tabs = [
  MonthlyParkerTab.Active,
  MonthlyParkerTab.Applied,
  MonthlyParkerTab.Waiting,
  MonthlyParkerTab.Invited,
  MonthlyParkerTab.Waitlist,
];

export function FacilityParkerListPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const last = loc.pathname.split("/").pop();
  const tab = tabs.includes(last as MonthlyParkerTab)
    ? (last as MonthlyParkerTab)
    : MonthlyParkerTab.Active;

  const { facilityId } = useParamIds();
  const [gridResult, waitlist] = useLoaderData() as [
    ParkerDetailsGridResult,
    ServerRequestsWaitlistData[]
  ];
  const parkers = gridResult.parkerDetailsGrid;
  let isExpired = false;

  // State for dialogs and actions
  const [showInviteLinkDialog, setShowInviteLinkDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [showResendConfirmDialog, setShowResendConfirmDialog] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState<GridParker | null>(null);
  const [inviteLink, setInviteLink] = useState("");
  const [invitedParkers, setInvitedParkers] = useState<GridParker[]>([]);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [activeVisibleCount, setActiveVisibleCount] = useState(10);
  const [appliedVisibleCount, setAppliedVisibleCount] = useState(10);
  const [invitedVisibleCount, setInvitedVisibleCount] = useState(10);

  const clickableDefaultColDef = useMemo(
    () => ({
      cellStyle: { cursor: "pointer" },
    }),
    []
  );

  const activeColumns = useMemo<ColDef<GridParker>[]>(
    () => [
      { field: "parkerId", headerName: "#" },
      {
        field: "name",
        headerName: "Name",
        cellRenderer: (params: any) => {
          const isInactive = params.data.planCount == 0;
          return (
            <span className="d-flex align-items-center gap-1">
              {isInactive && (
                <Badge bg="secondary" className="me-1">
                  Inactive
                </Badge>
              )}
              {params.value}
            </span>
          );
        },
      },
      {
        ...colNumber("planCount"),
        headerName: "Plans",
        type: "rightAligned",
        cellRenderer: (params: any) => {
          return (
            <div className="d-flex align-items-center justify-content-end">
              {!params.data.hasNonRenewedPeriod && params.data.hasAutoPay && (
                <Badge className="ms-2 me-2">Auto</Badge>
              )}

              {params.data.hasNonRenewedPeriod && (
                <Badge className="ms-2 me-2 bg-opacity-75">
                  <ExclamationCircleFill className="me-1 icon-shift-up" />
                  Renew Failed
                </Badge>
              )}

              {params.value}
            </div>
          );
        },
      },
      { ...colNumber("guestCount"), headerName: "Parkers" },
      { ...colNumber("spotCount") },
      {
        field: "balance",
        headerName: "Balance",
        type: "rightAligned",
        cellRenderer: (params: any) => {
          if (!params.value) {
            return <></>;
          }
          const balance = params.value;
          const isPositive = toCents(balance) > 0;
          const tokenClass = isPositive
            ? "bg-danger bg-opacity-10"
            : "bg-success bg-opacity-10";
          const textClass = isPositive ? "text-danger" : "text-success";

          return (
            <div className={`d-flex align-items-center justify-content-end`}>
              <span
                className={`badge ${tokenClass} ${textClass} px-2 py-1 mt-2 fs-6`}
              >
                {showMoney(balance)}
              </span>
            </div>
          );
        },
        comparator: compareMoney,
      },
      { ...colMoney("mrr"), headerName: "MRR" },
      { field: "note", headerName: "Note" },
    ],
    []
  );

  const appliedColumns = useMemo<ColDef<GridParker>[]>(
    () => [
      { field: "parkerId", headerName: "#" },
      {
        field: "name",
        headerName: "Name",
        cellRenderer: (params: any) => {
          isExpired = params.data.isExpired === true;
          return (
            <span className="d-flex align-items-center gap-1">
              {isExpired && (
                <Badge bg="secondary" className="me-1">
                  Expired
                </Badge>
              )}
              {params.value}
            </span>
          );
        },
      },
      { field: "emailAddress", headerName: "Email" },
      { field: "spotCount", headerName: "Spots" },
      {
        ...colInstantDateTime("createdWhen", (x) => gridResult.timeZone),
        headerName: "Created Date",
      },    
      { ...colEnum("status", (x) => showSubscriptionStatus(x as SubscriptionStatus)), headerName: "Status" },
      {
        field: "mrr",
        headerName: "Price",
        type: "rightAligned",
        valueFormatter: (x) => showMoney(x.value),
      },
      { field: "planName", headerName: "Plan" },
    ],
    []
  );

  const activeParkers = useMemo(
    () => parkers.filter((s) => 
      (s.planCount > 0 && s.status == SubscriptionStatus.Active) || 
      (showInactive && s.planCount == 0)
    ),
    [parkers, showInactive]
  );
  const applied = useMemo(
    () => parkers.filter((s) => s.status == SubscriptionStatus.Applied || s.status == SubscriptionStatus.NeedsSetup),
    [parkers]
  );
  const waiting = useMemo(
    () =>
      parkers.filter((s) => s.status == SubscriptionStatus.WaitingForContract),
    [parkers]
  );
  const invited = useMemo(
    () => parkers.filter((s) => s.status == SubscriptionStatus.Invited),
    [parkers]
  );

  // Initialize invitedParkers state
  useEffect(() => {
    setInvitedParkers(invited);
  }, [invited]);

  const onGridReady = (params: GridReadyEvent) => {
    params.api.sizeColumnsToFit();
  };

  const onRowClicked = (event: RowClickedEvent<any>) => {
    //fixed clicking the footer row
    if (event.rowPinned) {
      return;
    }

    // navigation only; global mask handled elsewhere

    if (tab === MonthlyParkerTab.Active) {
      const data: ParkerListEntry = event.data;
      nav(`/facility/${facilityId}/parker/${event.data.parkerId}/overview`, {
        state: { from: "parker" },
      });
    } else if (
      tab === MonthlyParkerTab.Applied ||
      tab === MonthlyParkerTab.Waiting
    ) {
      const data: FacilitySub = event.data;
      nav(
        `/facility/${facilityId}/parker/${data.parkerId}/plan/${data.subscriptionId}`,
        { state: { from: "parker", isExpired: event.data.isExpired } }
      );
    }
  };

  // Action handlers for invite actions
  const handleGetLink = async (parker: GridParker) => {
    if (!parker.inviteId) {
      toast.error("Invalid invite data");
      return;
    }
    setSelectedInvite(parker);
    try {
      const result = await Api.facilityParkerInviteAction(
        facilityId,
        parker.parkerId,
        parker.inviteId,
        "link"
      );
      processResult(
        result,
        (link: string) => {
          setInviteLink(link);
          setShowInviteLinkDialog(true);
        },
        (error: string) => {
          toast.error(`Failed to get invite link: ${error}`);
        }
      );
    } catch (error) {
      toast.error("Failed to get invite link");
    } finally {
    }
  };

  const handleResend = async (parker: GridParker) => {
    if (!parker.inviteId) {
      toast.error("Invalid invite data");
      return;
    }
    setSelectedInvite(parker);
    setShowResendConfirmDialog(true);
  };

  const confirmResend = async () => {
    if (!selectedInvite || !selectedInvite.inviteId) return;

    try {
      const result = await Api.facilityParkerInviteAction(
        facilityId,
        selectedInvite.parkerId,
        selectedInvite.inviteId,
        "resend"
      );
      processResult(
        result,
        () => {
          toast.success("Invite resent successfully");
          setShowResendConfirmDialog(false);
          setSelectedInvite(null);
        },
        (error: string) => {
          toast.error(`Failed to resend invite: ${error}`);
        }
      );
    } catch (error) {
      toast.error("Failed to resend invite");
    } finally {
    }
  };

  const handleDelete = async (parker: GridParker) => {
    if (!parker.inviteId) {
      toast.error("Invalid invite data");
      return;
    }
    setSelectedInvite(parker);
    setShowDeleteConfirmDialog(true);
  };

  const confirmDelete = async () => {
    if (!selectedInvite || !selectedInvite.inviteId) return;

    try {
      const result = await Api.facilityParkerInviteAction(
        facilityId,
        selectedInvite.parkerId,
        selectedInvite.inviteId,
        "delete"
      );
      processResult(
        result,
        () => {
          toast.success("Invite deleted successfully");
          // Remove the deleted invite from the list
          setInvitedParkers((prev) =>
            prev.filter((p) => p.inviteId !== selectedInvite.inviteId)
          );
          setShowDeleteConfirmDialog(false);
          setSelectedInvite(null);
        },
        (error: string) => {
          toast.error(`Failed to delete invite: ${error}`);
        }
      );
    } catch (error) {
      toast.error("Failed to delete invite");
    } finally {
    }
  };

  const invitedColumns = useMemo<ColDef<GridParker>[]>(
    () => [
      { field: "parkerId", headerName: "#" },
      { field: "name", headerName: "Name" },
      { field: "emailAddress", headerName: "Email" },
      {
        ...colInstantDateTime("createdWhen", (x) => gridResult.timeZone),
        headerName: "Invited Date",
      },
      { field: "planName", headerName: "Plan" },
      {
        field: "unitPrice",
        headerName: "Price",
        type: "rightAligned",
        valueFormatter: (x) => showMoney(x.value),
      },
      {
        headerName: "Actions",
        cellRenderer: (params: any) => {
          const parker = params.data as GridParker;
          return (
            <div className="d-flex gap-1">
              <button
                className="btn btn-sm"
                title="Get Invite Link"
                onClick={(e) => {
                  e.stopPropagation();
                  handleGetLink(parker);
                }}
              >
                <Link45deg />
              </button>
              <button
                className="btn btn-sm"
                title="Resend Invite"
                onClick={(e) => {
                  e.stopPropagation();
                  handleResend(parker);
                }}
              >
                <Envelope />
              </button>
              <button
                className="btn btn-sm"
                title="Delete Invite"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(parker);
                }}
              >
                <Trash />
              </button>
            </div>
          );
        },
        sortable: false,
        filter: false,
        width: 120,
      },
    ],
    [facilityId, handleGetLink, handleResend, handleDelete]
  );

  const formatCurrency = (val: any) => {
    if (val == null) return "";
    if (typeof val === "number")
      return val.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
    if (typeof val === "string") return val;
    if (val.toJSON) return formatCurrency(val.toJSON());
    if (val.formatter) return val.formatter; // fallback
    return String(val);
  };

  const footer = useMemo(() => {
    const totalGuests = activeParkers
      .map((p) => p.guestCount)
      .reduce((a, b) => a + b, 0);

    const totalBal = activeParkers
      .map((p) => p.balance)
      .reduce((a, b) => add(a, b), cents(0));

    const totalMrr = activeParkers
      .map((p) => p.mrr)
      .reduce((a, b) => add(a, b), cents(0));

    return [
      {
        spots: activeParkers.map((p) => p.spotCount).reduce((a, b) => a + b, 0),
        balance: totalBal,
        mrr: totalMrr,
      },
    ];
  }, [activeParkers]);

  const filterBySearch = (list: any[]) =>
    list.filter((p) => p.name?.toLowerCase().includes(search.toLowerCase()));

  const activeFiltered = filterBySearch(activeParkers);
  const activeShown = activeFiltered.slice(0, activeVisibleCount);

  const appliedFiltered = filterBySearch(applied);
  const appliedShown = appliedFiltered.slice(0, appliedVisibleCount);

  const invitedFiltered = filterBySearch(invitedParkers);
  const invitedShown = invitedFiltered.slice(0, invitedVisibleCount);

  return (
    <Container fluid className="h-100" style={{ paddingLeft: 0, paddingRight: 0 }}>
      <Row className="h-100" style={{ marginLeft: "10px", marginRight: 0 }}>
        <Col
          className="h-100"
          style={{
            display: "grid",
            gridTemplateRows: "auto auto 1fr 5px",
            gridAutoColumns: "1fr",
            overflow: "hidden",
            paddingLeft: 0,
            paddingRight: 0,
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <PageTitle>
              <span className="d-none d-sm-inline">Monthly Parkers</span>
              <span className="d-inline d-sm-none">Monthly</span>
            </PageTitle>

            <ToolButton
              onClick={() => nav(`/facility/${facilityId}/parker/invite`)}
            >
              <Asterisk color="maroon" />
              <span className="d-none d-sm-inline">Invite</span>
              <span className="d-inline d-sm-none">Invite</span>
            </ToolButton>

            <ToolButton
              onClick={() => nav(`/facility/${facilityId}/parker/import`)}
            >
              <PersonAdd color="maroon" />
              <span className="d-none d-sm-inline">Import Parkers</span>
              <span className="d-inline d-sm-none">Import</span>
            </ToolButton>
          </div>

          <Tabs
            defaultActiveKey={tab}
            activeKey={tab}
            onSelect={(k) => nav(`/facility/${facilityId}/parker/${k}`)}
            className="w-100 border-bottom-0"
            variant="underline"
            justify
          >
            <Tab
              eventKey={MonthlyParkerTab.Active}
              title="Active"
              className="h-100"
            >
              <div
                className="d-none d-md-block"
                style={{ height: "100%", width: "100%" }}
              >
                <DataGrid
                  rowData={activeParkers}
                  columnDefs={activeColumns}
                  onGridReady={onGridReady}
                  onRowClicked={onRowClicked}
                  pinnedBottomRowData={footer}
                  defaultColDef={clickableDefaultColDef}
                  headerControls={
                    <Form.Check
                      type="checkbox"
                      id="show-inactive-desktop"
                      label="Show Inactive"
                      checked={showInactive}
                      onChange={(e) => setShowInactive(e.target.checked)}
                    />
                  }
                />
              </div>

              <div className="d-block d-md-none">
                <div className="mb-3 mx-2 mt-3 sticky-top bg-white">
                  <Form.Control
                    type="text"
                    placeholder="Search parkers..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setActiveVisibleCount(10);
                    }}
                    className="mb-2"
                  />
                  <Form.Check
                    type="checkbox"
                    id="show-inactive-mobile"
                    label="Show Inactive"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                  />
                </div>

                {activeShown.length === 0 ? (
                  <div className="text-center text-muted py-3">
                    No Rows to Show
                  </div>
                ) : (
                  activeShown.map((p, idx) => {
                    const isInactive = p.status === SubscriptionStatus.Cancelled;
                    return (
                      <Card
                        key={idx}
                        className="mb-3 p-2 border rounded-4 border-1 mx-1"
                        style={{ cursor: "pointer" }} 
                        onClick={() => {
                          nav(
                            `/facility/${facilityId}/parker/${p.parkerId}/overview`,
                            {
                              state: { from: "parker" },
                            }
                          );
                        }}
                      >
                        <Card.Body className="p-2 p-md-3">
                          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                            <div className="d-flex align-items-center gap-1">
                              {isInactive && (
                                <Badge bg="secondary" className="me-1">
                                  Inactive
                                </Badge>
                              )}
                              <h6 className="mb-0 fw-bold">{p.name}</h6>
                            </div>
                            {p.hasNonRenewedPeriod && (
                              <Badge className="ms-2 mt-2 mt-sm-0 bg-opacity-75 d-flex align-items-center">
                                <ExclamationCircleFill className="me-1" />{" "}
                                Renew Failed
                              </Badge>
                            )}
                          </div>

                          <div className="row g-2 text-center">
                            <div className="col-6 col-sm-3">
                              <div className="bg-light rounded-3 py-2 px-2">
                                <div className="text-muted small">PLANS</div>
                                <div className="fw-bold fs-5">
                                  {p.planCount}
                                </div>
                              </div>
                            </div>

                            <div className="col-6 col-sm-3">
                              <div className="bg-light rounded-3 py-2 px-2">
                                <div className="text-muted small">
                                  SPOT COUNT
                                </div>
                                <div className="fw-bold fs-5">
                                  {p.spotCount}
                                </div>
                              </div>
                            </div>

                            <div className="col-6 col-sm-3">
                              <div
                                className="rounded-3 py-2 px-2"
                                style={{
                                  backgroundColor:
                                    toCents(p.balance) > 0
                                      ? "#fdf2f2"
                                      : "#f0fdf4",
                                }}
                              >
                                <div className="text-muted small mb-1">
                                  BALANCE
                                </div>
                                {p.balance ? (
                                  (() => {
                                    const balance = p.balance;
                                    const isPositive = toCents(balance) > 0;
                                    const tokenClass = isPositive
                                      ? "bg-danger bg-opacity-10"
                                      : "bg-success bg-opacity-10";
                                    const textClass = isPositive
                                      ? "text-danger"
                                      : "text-success";
                                    return (
                                      <span
                                        className={`badge ${tokenClass} ${textClass} px-2 py-1 fs-6`}
                                      >
                                        {showMoney(balance)}
                                      </span>
                                    );
                                  })()
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </div>
                            </div>

                            <div className="col-6 col-sm-3">
                              <div
                                className="rounded-3 py-2 px-2"
                                style={{ backgroundColor: "#f0fdf4" }}
                              >
                                <div className="text-muted small mb-1">MRR</div>
                                <div className="fw-bold fs-5 text-success">
                                  {showMoney(p.mrr)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })
                )}

                {activeVisibleCount < activeFiltered.length &&
                  activeShown.length > 0 && (
                    <div className="text-center">
                      <Button
                        className="btn-main"
                        onClick={() => setActiveVisibleCount((v) => v + 10)}
                      >
                        Load More
                      </Button>
                    </div>
                  )}
              </div>
            </Tab>

            <Tab
              eventKey={MonthlyParkerTab.Applied}
              title={
                <div className="d-flex-align-center justify-content-center">
                  Pending{" "}
                  {applied.length !== 0 && (
                    <Badge className="rounded-5 d-flex fw-normal justify-content-center px-2">
                      {applied.length}
                    </Badge>
                  )}
                </div>
              }
              className="h-100"
            >
              <div
                className="d-none d-md-block"
                style={{ height: "85%", width: "100%" }}
              >
                <div className="alert mt-3 alert-light-bg">
                  Applications will expire after <strong>7 days</strong>.
                  Parkers will need to re-apply after expiration. Expired
                  applications will be hidden after <strong>30 days</strong>.
                </div>
                <DataGrid
                  rowData={applied}
                  columnDefs={appliedColumns}
                  onGridReady={onGridReady}
                  onRowClicked={onRowClicked}
                  defaultColDef={clickableDefaultColDef}
                />
              </div>

              <div className="d-block d-md-none">
                <Form.Control
                  type="text"
                  placeholder="Search parkers..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setAppliedVisibleCount(10);
                  }}
                  className="mb-3 mt-3 sticky-top bg-white"
                />

                {appliedShown.length === 0 ? (
                  <div className="text-center text-muted py-3">
                    No Rows to Show
                  </div>
                ) : (
                  appliedShown.map((p, idx) => (
                    <Card
                      key={idx}
                      className="mb-3 mx-1 p-2 rounded-4 border-1"
                      style={{ cursor: "pointer" }} 
                      onClick={() => {
                        nav(
                          `/facility/${facilityId}/parker/${p.parkerId}/plan/${p.subscriptionId}`,
                          { state: { from: "parker", isExpired: p.isExpired } }
                        );
                      }}
                    >
                      <Card.Body className="p-2 p-md-3">
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                          <div className="d-flex align-items-center gap-1">
                            {p.isExpired && (
                              <Badge bg="secondary" className="me-1">
                                Expired
                              </Badge>
                            )}
                         
                              <Badge bg="primary" className="me-1">
                                {showSubscriptionStatus(p.status as SubscriptionStatus)}
                              </Badge>
                           
                            <h6 className="mb-0 fw-bold">{p.name}</h6>
                          </div>                     
                        </div>

                        <div className="row g-2 text-center">
                          <div className="col-4">
                            <div className="bg-light rounded-3 py-3">
                              <div className="text-muted small">SPOTS</div>
                              <div className="fw-bold fs-5">{p.spotCount}</div>
                            </div>
                          </div>

                          <div className="col-4">
                            <div className="bg-light rounded-3 py-3">
                              <div className="text-muted small">
                                CREATE DATE
                              </div>
                              <div className="fw-bold fs-6">
                                {p.createdWhen
                                  ? new Date(p.createdWhen).toLocaleDateString(
                                      "en-US",
                                      {
                                        timeZone: gridResult.timeZone,
                                      }
                                    )
                                  : ""}
                              </div>
                            </div>
                          </div>

                          <div className="col-4">
                            {(() => {
                              const total = p.mrr;
                              const tileBg = "#f0fdf4";
                              const tokenClass = "bg-success bg-opacity-10";
                              const textClass = "text-success";

                              return (
                                <div
                                  className="rounded-3 py-3 text-center"
                                  style={{ backgroundColor: tileBg }}
                                >
                                  <div className="text-muted small mb-1">
                                    TOTAL PRICE
                                  </div>
                                  <span
                                    className={`badge ${tokenClass} ${textClass} px-2 py-1 fs-6`}
                                  >
                                    {showMoney(total)}
                                  </span>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))
                )}

                {appliedVisibleCount < appliedFiltered.length &&
                  appliedShown.length > 0 && (
                    <div className="text-center">
                      <Button
                        className="btn-main"
                        onClick={() => setAppliedVisibleCount((v) => v + 10)}
                      >
                        Load More
                      </Button>
                    </div>
                  )}
              </div>
            </Tab>

            <Tab
              eventKey={MonthlyParkerTab.Invited}
              title={
                <div className="d-flex-align-center justify-content-center">
                  Invited{" "}
                  {invitedParkers.length !== 0 && (
                    <Badge className="rounded-5 d-flex fw-normal justify-content-center px-2">
                      {invitedParkers.length}
                    </Badge>
                  )}
                </div>
              }
              className="h-100"
            >
              <div
                className="d-none d-md-block"
                style={{ height: "100%", width: "100%" }}
              >
                <DataGrid
                  rowData={invitedParkers}
                  columnDefs={invitedColumns}
                  onGridReady={onGridReady}
                  className="ag-theme-alpine"
                />
              </div>

              <div className="d-block d-md-none">
                <Form.Control
                  type="text"
                  placeholder="Search parkers..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setInvitedVisibleCount(10);
                  }}
                  className="mb-3 mt-3 sticky-top bg-white"
                />
                {invitedShown.length === 0 ? (
                  <div className="text-center text-muted py-3">
                    No Rows to Show
                  </div>
                ) : (
                  <>
                    {invitedShown.map((p, idx) => (
                      <Card
                        key={idx}
                        className="mb-3 p-2 rounded-4 border-1 mx-1"
                      >
                        <Card.Body className="p-2 p-md-3">
                          <div className="d-flex flex-column mb-3">
                            <h6 className="mb-2 fw-bold">{p.name}</h6>

                            <div className="d-flex gap-2 flex-wrap">
                              <Button
                                size="sm"
                                className="btn-main flex-fill"
                                title="Get Invite Link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleGetLink(p);
                                }}
                              >
                                Get Link
                              </Button>

                              <Button
                                size="sm"
                                variant="light"
                                className="flex-fill"
                                title="Delete Invite"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(p);
                                }}
                              >
                                Delete
                              </Button>
                            </div>
                          </div>

                          <div className="row g-2 text-center">
                            <div className="col-4">
                              <div className="bg-light rounded-3 py-2 px-2">
                                <div className="text-muted small">
                                  INVITED DATE
                                </div>
                                <div className="fw-bold fs-6">
                                  {p.createdWhen
                                    ? new Date(
                                        p.createdWhen
                                      ).toLocaleDateString("en-US")
                                    : "—"}
                                </div>
                              </div>
                            </div>

                            <div className="col-4">
                              <div className="bg-light rounded-3 py-2 px-2">
                                <div className="text-muted small">PLAN</div>
                                <div className="fw-bold fs-6">
                                  {p.planName || "—"}
                                </div>
                              </div>
                            </div>

                            <div className="col-4">
                              {(() => {
                                const bgColor = "#f0fdf4";
                                const badgeClass =
                                  "badge bg-success bg-opacity-10 text-success px-2 py-1 fs-6";

                                return (
                                  <div
                                    className="rounded-3 py-2 px-2"
                                    style={{ backgroundColor: bgColor }}
                                  >
                                    <div className="text-muted small mb-1">
                                      PRICE
                                    </div>
                                    <span className={badgeClass}>
                                      {showMoney(p.unitPrice)}
                                    </span>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                  </>
                )}

                {invitedVisibleCount < invitedFiltered.length &&
                  invitedShown.length > 0 && (
                    <div className="text-center">
                      <Button
                        className="btn-main"
                        onClick={() => setInvitedVisibleCount((v) => v + 10)}
                      >
                        Load More
                      </Button>
                    </div>
                  )}
              </div>
            </Tab>

            <Tab
              eventKey={MonthlyParkerTab.Waitlist}
              title={
                <div className="d-flex-align-center justify-content-center">
                  Waitlist{" "}
                  {waitlist.length !== 0 && (
                    <Badge className="rounded-5 d-flex fw-normal justify-content-center px-2">
                      {waitlist.length}
                    </Badge>
                  )}
                </div>
              }
              className="h-100"
            >
              <div style={{ height: "100%", width: "100%" }}>
                <WaitlistManagementPage />
              </div>
            </Tab>
          </Tabs>
        </Col>
      </Row>

      <InviteLinkDialog
        show={showInviteLinkDialog}
        onHide={() => setShowInviteLinkDialog(false)}
        inviteLink={inviteLink}
        parkerName={selectedInvite?.name || ""}
        parkerEmail={selectedInvite?.emailAddress || ""}
      />

      <ConfirmDialog
        show={showDeleteConfirmDialog}
        onHide={() => setShowDeleteConfirmDialog(false)}
        onConfirm={confirmDelete}
        title="Delete Invite"
        htmlMessage={`Are you sure you want to delete the invite for <strong>${selectedInvite?.name}</strong>? This action cannot be undone.<br /><br /> <span class="text-muted">To invite this parker again, you will need to create a new invite.</span>`}
        confirmText="Delete"
      />

      <ConfirmDialog
        show={showResendConfirmDialog}
        onHide={() => setShowResendConfirmDialog(false)}
        onConfirm={confirmResend}
        title="Resend Invite"
        htmlMessage={`Are you sure you want to resend the invite to <strong>${selectedInvite?.emailAddress}</strong>?`}
        confirmText="Resend"
      />
    </Container>
  );
}